// Payload builders for the Applicant Additional Details step
// (extended journey, activeHorizontalIndex === 3 && activeVerticalIndex === 0).
import dayjs from "dayjs";
import { resolveBirthCityId } from "../../../utils/helpers";

type NationalityResolver = (rawValue: any) => { value?: any; label?: any } | null;

const toValue = (val: any) => {
  if (val && typeof val === "object" && "value" in val) {
    return val.value;
  }
  return val;
};

const toLabel = (val: any) => {
  if (val && typeof val === "object" && "label" in val) {
    return val.label;
  }
  return val;
};

const pickValue = (...candidates: any[]) =>
  candidates.find(
    (candidate) =>
      candidate !== undefined &&
      candidate !== null &&
      String(candidate).trim() !== "",
  );

const findField = (fields: any[], pattern: RegExp) =>
  (fields || []).find((f: any) =>
    pattern.test(String(f?.label || "") + String(f?.name || "")),
  );

const getFieldValue = (currentValues: any, fieldObj: any) =>
  fieldObj ? currentValues?.[fieldObj.name] : "";

const toDateOfBirthMillis = (dobRaw: any) => {
  if (typeof dobRaw === "number") return dobRaw;
  const numericDob = Number(dobRaw);
  if (Number.isFinite(numericDob) && String(dobRaw).length >= 10) {
    return numericDob;
  }
  const direct = dayjs(dobRaw);
  if (direct.isValid()) return direct.valueOf();
  const dobMatch = String(dobRaw).match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dobMatch) {
    const [, dd, mm, yyyy] = dobMatch;
    const parsed = dayjs(`${yyyy}-${mm}-${dd}`);
    return parsed.isValid() ? parsed.valueOf() : "";
  }
  return "";
};

// Resolve the Applicant Additional Details fields that are matched by label/name
export const getApplicantAdditionalFields = (fields: any[]) => ({
  dependentsField: findField(fields, /dependents?/i),
  maritalField: findField(fields, /marital/i),
  birthCountryField: findField(fields, /country.*birth|birth.*country/i),
  birthCityField: findField(fields, /city.*birth|birth.*city/i),
  loanAmountField: findField(
    fields,
    /required\s*loan\s*amount|loan\s*amount/i,
  ),
  loanTenureField: findField(
    fields,
    /requested\s*loan\s*tenure|loan\s*tenure|tenure/i,
  ),
  loanRoiField: findField(fields, /requested\s*roi|\broi\b/i),
  disbursementDateField: findField(
    fields,
    /expected\s*date\s*of\s*disbursement|disbursement/i,
  ),
});

// createApplicant `data` array for the Applicant Additional Details step
export const buildApplicantAdditionalFormData = (
  fields: any[],
  currentValues: any,
  resolveApplicantNationalityOption: NationalityResolver,
) =>
  (fields || [])
    .filter((field: any) => field?.id)
    .map((field: any) => {
      const rawValue = currentValues?.[field.name];
      const isNationalityField =
        /nationality/i.test(String(field?.name || "")) ||
        /naionality/i.test(String(field?.name || ""));
      const isBirthCityField =
        /city.*birth|birth.*city/i.test(
          String(field?.label || "") + String(field?.name || ""),
        ) || String(field?.name || "").toLowerCase() === "applicantcitybirth";
      const nationalityOption = isNationalityField
        ? resolveApplicantNationalityOption(rawValue)
        : null;
      const normalizedValue =
        rawValue && typeof rawValue === "object"
          ? isNationalityField
            ? nationalityOption?.label ||
              ("label" in rawValue
                ? rawValue.label
                : "value" in rawValue
                  ? rawValue.value
                  : rawValue)
            : isBirthCityField
              ? "label" in rawValue
                ? rawValue.label
                : "value" in rawValue
                  ? rawValue.value
                  : rawValue
              : "value" in rawValue
                ? rawValue.value
                : "label" in rawValue
                  ? rawValue.label
                  : rawValue
          : isNationalityField
            ? nationalityOption?.label || rawValue
            : rawValue;

      return {
        formFieldId: field.id,
        formFieldKey: field.name,
        formFieldValue: String(normalizedValue ?? ""),
      };
    });

// createUpdateApplicantBasicDetails payload (APPLICANT_ADDITIONAL_BASIC_DETAILS_SAVED)
export const buildAdditionalBasicDetailsPayload = ({
  fields,
  currentValues,
  formData,
  dropdownData,
  birthCityList,
  resolveApplicantNationalityOption,
  applicantId,
  applicationId,
  trackingId,
}: {
  fields: any[];
  currentValues: any;
  formData: any;
  dropdownData: any;
  birthCityList: any[];
  resolveApplicantNationalityOption: NationalityResolver;
  applicantId: number;
  applicationId: number;
  trackingId: string;
}) => {
  const { dependentsField, maritalField, birthCountryField, birthCityField } =
    getApplicantAdditionalFields(fields);

  const dependents = toValue(getFieldValue(currentValues, dependentsField)) || "";
  const maritalStatusId =
    toValue(getFieldValue(currentValues, maritalField)) || "";
  const birthCountryIdValue =
    toValue(getFieldValue(currentValues, birthCountryField)) || "";
  const birthCountryId =
    (dropdownData?.countries || []).find(
      (opt: any) => String(opt?.label || "") === String(birthCountryIdValue || ""),
    )?.value || "";
  const birthCityId = resolveBirthCityId(
    getFieldValue(currentValues, birthCityField),
    birthCityList,
  );

  const firstName =
    pickValue(
      currentValues?.firstName,
      currentValues?.studentDetailsFirstName,
      formData?.firstName,
      formData?.studentDetailsFirstName,
    ) || "";
  const middleName =
    pickValue(
      currentValues?.middleName,
      currentValues?.studentDetailsMiddleName,
      formData?.middleName,
      formData?.studentDetailsMiddleName,
    ) || "";
  const lastName =
    pickValue(
      currentValues?.lastName,
      currentValues?.studentDetailsLastName,
      formData?.lastName,
      formData?.studentDetailsLastName,
    ) || "";
  const fullname =
    pickValue(
      currentValues?.fullname,
      currentValues?.fullName,
      formData?.fullname,
      formData?.fullName,
    ) ||
    [firstName, middleName, lastName].filter(Boolean).join(" ").trim();

  const emailAddress =
    pickValue(
      currentValues?.email,
      currentValues?.studentDetailEmail,
      formData?.email,
      formData?.studentDetailEmail,
    ) || "";
  const mobileNo =
    pickValue(
      currentValues?.phone_number,
      currentValues?.mobileNo,
      formData?.phone_number,
      formData?.mobileNo,
    ) || "";

  const salutationId =
    toValue(pickValue(currentValues?.salutation, formData?.salutation) || "") ||
    "";
  const salutation = (dropdownData?.salutations ?? []).find(
    (item: any) => item?.value === salutationId,
  )?.label;

  const relationshipRaw =
    pickValue(currentValues?.relationship, formData?.relationship) || "";
  const relationshipId = toValue(relationshipRaw) || "";
  const relationship = toLabel(relationshipRaw) || relationshipId || "STUDENT";

  const nationalityId = resolveApplicantNationalityOption(
    pickValue(currentValues?.applicantNationality, formData?.applicantNationality) ||
      "",
  )?.value;

  const dateOfBirth = toDateOfBirthMillis(
    pickValue(
      currentValues?.dob,
      currentValues?.dateOfBirth,
      formData?.dob,
      formData?.dateOfBirth,
    ) || "",
  );

  return {
    applicantId,
    applicationId,
    trackingId,
    applicantType: "MAIN_APPLICANT",
    //isAddingViaApplicationFlow: true,
    emailAddress,
    firstName,
    fullname,
    lastName,
    //middleName: middleName,
    dateOfBirth,
    mobileNo,
    salutation,
    salutationId,
    queryId: "",
    relationship,
    relationshipId: "CHILDREN",
    eventName: "APPLICANT_ADDITIONAL_BASIC_DETAILS_SAVED",
    noOfDependants: Number(dependents || 0),
    maritalStatusId,
    nationalityId,
    birthCountryId,
    birthCityId,
    otherRelationship: null,
    motherFirstName: currentValues?.motherFirstName || formData?.motherFirstName,
    motherLastName: currentValues?.motherLastName || formData?.motherLastName,
    fatherFirstName: currentValues?.fatherFirstName || formData?.fatherFirstName,
    fatherLastName: currentValues?.fatherLastName || formData?.fatherLastName,
  };
};

const getLoanRequirementValues = (fields: any[], currentValues: any) => {
  const { loanAmountField, loanTenureField, loanRoiField, disbursementDateField } =
    getApplicantAdditionalFields(fields);

  const disbursementRaw = getFieldValue(currentValues, disbursementDateField);
  return {
    requiredLoanAmount: toValue(getFieldValue(currentValues, loanAmountField)),
    requestedLoanTenure: toValue(getFieldValue(currentValues, loanTenureField)),
    requestedRoi: toValue(getFieldValue(currentValues, loanRoiField)),
    expectedDateOfDisbursement:
      disbursementRaw && dayjs(disbursementRaw).isValid()
        ? dayjs(disbursementRaw).format("YYYY-MM-DD")
        : toValue(disbursementRaw) || "",
  };
};

// updateLead payload for the Applicant Additional Details step (currently not sent)
export const buildApplicantAdditionalUpdateLeadPayload = (
  fields: any[],
  currentValues: any,
  trackingId: string,
) => {
  const {
    requiredLoanAmount,
    requestedLoanTenure,
    requestedRoi,
    expectedDateOfDisbursement,
  } = getLoanRequirementValues(fields, currentValues);

  return {
    trackingId: trackingId || "",
    amountRequested: requiredLoanAmount || "",
    requestedLoanTenure: requestedLoanTenure || "",
    requestedROI: requestedRoi || "",
    requestedRoi: requestedRoi || "",
    expectedDateOfDisbursement: expectedDateOfDisbursement || "",
    requiredLoanAmount: requiredLoanAmount || "",
  };
};

// saveLoanRequirement payload (APPLICANT_SAVE_LOAN_REQUIREMENT_ADDED)
export const buildSaveLoanRequirementPayload = ({
  fields,
  currentValues,
  formData,
  dropdownData,
  isDIYJourney,
  applicationId,
  trackingId,
}: {
  fields: any[];
  currentValues: any;
  formData: any;
  dropdownData: any;
  isDIYJourney: boolean;
  applicationId: number;
  trackingId: string;
}) => {
  const {
    requiredLoanAmount,
    requestedLoanTenure,
    requestedRoi,
    expectedDateOfDisbursement,
  } = getLoanRequirementValues(fields, currentValues);

  const requestloantenureValue = dropdownData?.loanTenureVal?.filter(
    (item: any) => String(item?.value) === String(requestedLoanTenure),
  )[0]?.id;
  // DIY journey: Requested Tenure is hidden, so resolve the
  // LOAN_TENURE_INMONTH system-value id whose value is 120
  // (dropdownData.loanTenureVal's `label` holds the raw month
  // count) instead of sending the raw number 120.
  const diyLoanTenureId = dropdownData?.loanTenureVal?.find(
    (item: any) => String(item?.label).trim() === "120",
  )?.id;

  return {
    applicationId: applicationId ?? 0,
    trackingId: trackingId || "",
    eventName: "APPLICANT_SAVE_LOAN_REQUIREMENT_ADDED",
    applicantType: "Applicant",
    amountRequested: Number(requiredLoanAmount) || 0,
    psiAmount: Number(currentValues?.psiAmount || formData?.psiAmount || 0),
    // DIY journey: Requested Tenure / Requested ROI are hidden
    // in the UI and hardcoded by the backend (120 / 13). The
    // loan-requirement API expects the LOAN_TENURE_INMONTH
    // system-value id, not the raw month count.
    // If formik already carries values for these fields (e.g.
    // restored from a saved application), keep them instead of
    // forcing the DIY defaults.
    loantenure: requestedLoanTenure
      ? Number(requestloantenureValue) || Number(requestedLoanTenure) || 0
      : isDIYJourney
        ? Number(diyLoanTenureId) || 120
        : Number(requestloantenureValue) || 0,
    requiredRoi: requestedRoi
      ? Number(requestedRoi) || 0
      : isDIYJourney
        ? 13
        : 0,
    queryId: "",
    caseType: currentValues?.caseType || formData?.caseType || "ELLOAN",
    expectedDateOfDisbursement: expectedDateOfDisbursement || "",
    promoCode: currentValues?.promoCode || formData?.promoCode || "",
    isSiblingTag: currentValues?.isSiblingTag || formData?.isSiblingTag || "",
  };
};

// ── Fund & Expense step (activeHorizontalIndex === 3 && activeVerticalIndex === 2) ──

const normalizeFieldText = (value: any) =>
  String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Parse a fund/expense value ("1,00,000", 5000, { value }) to a number; blanks/invalid -> 0
export const toFundExpenseNumber = (raw: any): number => {
  if (raw === null || raw === undefined || raw === "") return 0;
  if (typeof raw === "number") return isNaN(raw) ? 0 : raw;
  const candidate =
    typeof raw === "object" && raw !== null && "value" in raw
      ? (raw as any).value
      : raw;
  const normalized = String(candidate)
    .replace(/,/g, "")
    .replace(/[^0-9.-]/g, "")
    .trim();
  if (!normalized) return 0;
  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? 0 : parsed;
};

const startsWithPrefix = (name: any, prefixes: string[]) => {
  const n = normalizeFieldText(name);
  return prefixes.some((p) => n.startsWith(p) || n.startsWith(`ad${p}`));
};

const includesKeyword = (value: any, keywords: string[]) => {
  const n = normalizeFieldText(value);
  return keywords.some((k) => n.includes(k));
};

const computeSectionTotal = (
  sectionFields: any[],
  totalKeywords: string[],
  currentValues: any,
): number => {
  const isExact = (val: any) => {
    const n = normalizeFieldText(val);
    return totalKeywords.some((k) => n === k || n.endsWith(k) || n.startsWith(k));
  };
  const directField = sectionFields.find(
    (f: any) => isExact(f?.name) || isExact(f?.label),
  );
  const sum = sectionFields
    .map((f: any) => f?.name)
    .filter((n: string) => !!n && n !== directField?.name)
    .reduce(
      (acc: number, key: string) => acc + toFundExpenseNumber(currentValues?.[key]),
      0,
    );
  if (sum > 0) return sum;
  if (directField?.name) {
    const dv = toFundExpenseNumber(currentValues?.[directField.name]);
    if (dv > 0) return dv;
  }
  return sum;
};

// Total Fund / Total Expense used for the "must be equal" Continue validation
export const getFundExpenseTotals = (fields: any[], currentValues: any) => {
  const sortedFields = Array.isArray(fields) ? [...fields] : [];
  const expenseFields = sortedFields.filter(
    (f: any) =>
      startsWithPrefix(f?.name, ["expense", "expenses"]) ||
      includesKeyword(f?.name, ["totalexpense", "expense"]) ||
      includesKeyword(f?.label, ["totalexpense", "expense"]),
  );
  const fundFields = sortedFields.filter((f: any) =>
    startsWithPrefix(f?.name, ["fund", "fundrequest", "funds"]),
  );

  return {
    totalExpense: computeSectionTotal(
      expenseFields,
      ["totalexpense", "expensetotal"],
      currentValues,
    ),
    totalFund: computeSectionTotal(
      fundFields,
      ["totalfund", "fundtotal"],
      currentValues,
    ),
  };
};

export const isAdmissionStatusUndecided = (currentValues: any) =>
  String(toValue(currentValues?.admissionStatus) ?? "").toLowerCase() ===
  "undecided";

// createApplicant `data` array for the Fund & Expense step
export const buildFundExpenseFormData = (fields: any[], currentValues: any) => {
  const fundExpenseData = (fields || [])
    .filter((field: any) => field?.id)
    .map((field: any) => {
      const rawValue = currentValues?.[field.name];
      const normalizedValue =
        rawValue && typeof rawValue === "object"
          ? "value" in rawValue
            ? rawValue.value
            : "label" in rawValue
              ? rawValue.label
              : rawValue
          : rawValue;

      return {
        formFieldId: field.id,
        formFieldKey: field.name,
        formFieldValue: String(normalizedValue ?? 0),
      };
    });

  // Undecided admission: blank fund/expense fields are sent as 0
  if (isAdmissionStatusUndecided(currentValues)) {
    fundExpenseData.forEach((item: any) => {
      if (item.formFieldValue === "") {
        item.formFieldValue = "0";
      }
    });
  }

  return fundExpenseData;
};

// saveExpense payload (APPLICANT_APPLICATION_EXPENSE_FUNDS_DETAILS)
export const buildSaveExpensePayload = ({
  fields,
  currentValues,
  totalExpense,
  totalFund,
  trackingId,
  applicationId,
}: {
  fields: any[];
  currentValues: any;
  totalExpense: number;
  totalFund: number;
  trackingId: string;
  applicationId: string;
}) => {
  const getNumericByKeywords = (keys: string[], fallback = 0): number => {
    const matchedField = (fields || []).find((f: any) => {
      const normalizedName = normalizeFieldText(f?.name);
      const normalizedLabel = normalizeFieldText(f?.label);
      return keys.some(
        (key) => normalizedName.includes(key) || normalizedLabel.includes(key),
      );
    });
    if (!matchedField?.name) return fallback;
    return toFundExpenseNumber(currentValues?.[matchedField.name]);
  };

  return {
    trackingId,
    eventName: "APPLICANT_APPLICATION_EXPENSE_FUNDS_DETAILS",
    applicantType: "Applicant",
    applicationId: applicationId ?? 0,
    queryId: "",
    livingExpenses: toFundExpenseNumber(currentValues?.expenseLivingExpenses),
    livingCardExpenses: toFundExpenseNumber(currentValues?.expenseVasExpense),
    travelExpenses: toFundExpenseNumber(currentValues?.expenseTravelExpenses),
    otherExpenses: toFundExpenseNumber(currentValues?.expenseOtherExpenses),
    tuitionFees: toFundExpenseNumber(currentValues?.expenseTuitionFees),
    totalExpenses: getNumericByKeywords(
      ["totalexpense", "expensetotal"],
      totalExpense,
    ),
    loanRequestedAmt: toFundExpenseNumber(currentValues?.fundRequestedLoanAmount),
    ownSourceAmt: toFundExpenseNumber(currentValues?.fundOwnSources),
    scholarshipAmt: toFundExpenseNumber(currentValues?.fundScholarshipAmount),
    otherIncomeAmt: toFundExpenseNumber(currentValues?.fundOtherIncome),
    totalFundsRequiredAmt: getNumericByKeywords(
      ["totalfund", "fundtotal", "totalfundrequired"],
      totalFund,
    ),
  };
};
